import { RootState } from '../../../../../../init-store';
import { SELF } from '../../../../../shared/constants';
import { selectIsDrawConferenceDisabled } from '../../../../../shared/features/room-agent/dtmf/debug-menu/selectors';
import { isMobileUa } from '../../../../../shared/features/uatraits/selectors';
import { getCssCustomProperty } from '../../../../../shared/helpers/get-css-custom-property';
import { DEFAULT_RATIO } from '../../../../../shared/helpers/layout-manager/constants';
import { getGridLayout } from '../../../../../shared/helpers/layout-manager/grid-layout';
import { getPresenterDrawLayout } from '../../../../../shared/helpers/layout-manager/presenter-draw-layout';
import { Area, Size } from '../../../../../shared/helpers/layout-manager/types';
import { RolePermission } from '../../../../../shared/helpers/permissions';
import { GRID_VIEW } from '../../../constants';
import {
    getDesktopSharingStatus,
    getDominantSpeaker,
    getParticipant,
    getScreenSharingParticipant,
    getVisibleParticipants,
    selectIsNewGrid,
    selectIsPermissionsExtendedEnabled,
    selectVideoGrid,
} from '../../../selectors';
import { RecordSagaHelpers } from '../../../types';
import { hasRolePermission } from '../../permissions';
import { getParticipantRole } from '../../permissions/helpers';
import { collectStreamToVideosMap, drawParticipant } from './drawer';
import { OverlayManager } from './overlay-manager';
import { getRvfcOptimizer } from './rvfc-skip/optimizer';
import { ParticipantLayoutEntry } from './types';

const PRESENTER_MAX_COUNT = 6;

let overlayManager: OverlayManager | null = null;

export function clearOverlayManager() {
    overlayManager?.dispose();
    overlayManager = null;
}

/**
 * Двухпроходный рендеринг: участники + overlay через OverlayManager
 * (offscreen canvas с hash-based invalidation — перерисовывается только при
 * изменении релевантного состояния).
 */
export function drawConferenceCached(
    ctx: CanvasRenderingContext2D,
    size: Size,
    state: RootState,
    hideMyVideo: boolean,
    helpers: RecordSagaHelpers
) {
    const colorBackgroundDefault = getCssCustomProperty('--color-mg-surface-submerged');
    ctx.fillStyle = colorBackgroundDefault || '';
    ctx.fillRect(0, 0, size.width, size.height);

    const streamToVideosMap = collectStreamToVideosMap();
    const isDrawDisabled = selectIsDrawConferenceDisabled(state);
    const layout: ParticipantLayoutEntry[] = isDrawDisabled
        ? []
        : buildParticipantLayout(state, size, hideMyVideo, helpers, streamToVideosMap);

    if (!isDrawDisabled) {
        const visibleParticipants = getVisibleParticipants(state);
        const { currentPage, activeGridView } = selectVideoGrid(state);
        const isScreenSharingActive = getDesktopSharingStatus(state) !== 'none';

        getRvfcOptimizer()?.beforeDraw(visibleParticipants.length, activeGridView, currentPage, isScreenSharingActive);

        for (const entry of layout) {
            drawParticipant(
                ctx,
                entry.rawArea,
                entry.participant,
                entry.mode,
                streamToVideosMap,
                {
                    skipOverlay: true,
                    isDrawDesktopTrack: entry.isDrawDesktopTrack,
                    isDrawPresenter: entry.isDrawPresenter,
                    isPresenter: entry.isPresenter,
                    isNewGrid: entry.isNewGrid,
                    participantsCount: entry.participantsCount,
                    gridView: entry.gridView,
                    isMainSlot: entry.isMainSlot,
                    isMobile: entry.isMobile,
                },
                helpers
            );
        }
    }

    if (
        !overlayManager ||
        overlayManager.getSize().width !== size.width ||
        overlayManager.getSize().height !== size.height
    ) {
        overlayManager?.dispose();
        overlayManager = new OverlayManager(size);
    }
    ctx.drawImage(overlayManager.render(state, layout, size), 0, 0);
}

function buildParticipantLayout(
    state: RootState,
    size: Size,
    hideMyVideo: boolean,
    helpers: RecordSagaHelpers,
    streamToVideosMap: ReturnType<typeof collectStreamToVideosMap>
): ParticipantLayoutEntry[] {
    const isNewGrid = selectIsNewGrid(state);
    const isScreenSharingActive = getDesktopSharingStatus(state) !== 'none';
    const isPermissionsExtended = selectIsPermissionsExtendedEnabled(state);
    const margin = isNewGrid ? 4 : 0;
    const { currentPage, activeGridView } = selectVideoGrid(state);
    const visibleParticipants = getVisibleParticipants(state);
    const isMobile = isMobileUa(state);

    const entries: ParticipantLayoutEntry[] = [];

    let participantListLayout: Area[];
    if (activeGridView === GRID_VIEW.DEFAULT) {
        // Если кол-во участников не вмещается на одну страницу, то на других страницах
        // всегда показываем сетку на 12 человек.
        const layoutSize = currentPage > 0 ? 12 : visibleParticipants.length;
        const gridLayout = getGridLayout(
            size,
            visibleParticipants.length,
            layoutSize,
            DEFAULT_RATIO,
            isNewGrid,
            margin
        );
        participantListLayout = gridLayout.participantsListLayout;
    } else {
        const presenter = getScreenSharingParticipant(state) || getDominantSpeaker(state);
        const count = visibleParticipants.length;
        const isOnlyParticipant = count === 1;
        const isMyHiddenVideoOnly = hideMyVideo && count === 0;
        const isSelfPresenter = Boolean(presenter) && presenter?.id === SELF;
        const isOnlySelfParticipant = isOnlyParticipant && visibleParticipants[0]?.id === SELF && isSelfPresenter;
        const onlyPresenter = isMyHiddenVideoOnly || isOnlySelfParticipant;

        const presenterLayout = getPresenterDrawLayout({
            size,
            count: visibleParticipants.length,
            maxCount: PRESENTER_MAX_COUNT,
            isPresenterOnly: onlyPresenter,
            isNewGrid,
            isScreenSharingActive,
            margin,
        });
        participantListLayout = presenterLayout.participantsListLayout;

        if (presenter && !(hideMyVideo && onlyPresenter)) {
            const refParticipant = presenter.refParticipantId
                ? getParticipant(state, presenter.refParticipantId)
                : null;
            const role = getParticipantRole(presenter);
            const hasMicrophonePermission = hasRolePermission(state, RolePermission.MICROPHONE, role);

            entries.push(
                makeEntry({
                    participant: presenter,
                    refParticipant,
                    area: presenterLayout.presenterArea,
                    mode: 'contain',
                    isNewGrid,
                    isDrawDesktopTrack: true,
                    isDrawPresenter: true,
                    isPresenter: false,
                    isMainSlot: true,
                    streamToVideosMap,
                    helpers,
                    participantsCount: visibleParticipants.length,
                    gridView: activeGridView,
                    isMobile,
                    isPermissionsExtended,
                    hasMicrophonePermission,
                })
            );
        }
    }

    for (let i = 0; i < Math.min(visibleParticipants.length, participantListLayout.length); i += 1) {
        const participant = visibleParticipants[i];
        if (hideMyVideo && participant.id === SELF) {
            continue;
        }
        const refParticipant = participant.refParticipantId
            ? getParticipant(state, participant.refParticipantId)
            : null;
        const role = getParticipantRole(participant);
        const hasMicrophonePermission = hasRolePermission(state, RolePermission.MICROPHONE, role);

        entries.push(
            makeEntry({
                participant,
                refParticipant,
                area: participantListLayout[i],
                mode: 'cover',
                isNewGrid,
                isDrawDesktopTrack: activeGridView === GRID_VIEW.DEFAULT,
                isDrawPresenter: false,
                isPresenter: activeGridView === GRID_VIEW.DEFAULT,
                isMainSlot: false,
                streamToVideosMap,
                helpers,
                participantsCount: visibleParticipants.length,
                gridView: activeGridView,
                isMobile,
                isPermissionsExtended,
                hasMicrophonePermission,
            })
        );
    }

    return entries;
}

function makeEntry(args: {
    participant: ParticipantLayoutEntry['participant'];
    refParticipant: ParticipantLayoutEntry['refParticipant'];
    area: Area;
    mode: ParticipantLayoutEntry['mode'];
    isNewGrid: boolean;
    isDrawDesktopTrack: boolean;
    isDrawPresenter: boolean;
    isPresenter: boolean;
    isMainSlot: boolean;
    streamToVideosMap: ReturnType<typeof collectStreamToVideosMap>;
    helpers: RecordSagaHelpers;
    participantsCount: number;
    gridView: GRID_VIEW;
    isMobile: boolean;
    isPermissionsExtended: boolean;
    hasMicrophonePermission: boolean;
}): ParticipantLayoutEntry {
    const {
        participant,
        refParticipant,
        area,
        mode,
        isNewGrid,
        isDrawDesktopTrack,
        isDrawPresenter,
        isPresenter,
        isMainSlot,
        streamToVideosMap,
        helpers,
        participantsCount,
        gridView,
        isMobile,
        isPermissionsExtended,
        hasMicrophonePermission,
    } = args;

    // Padding из drawParticipant (drawer.ts:404) — 4px для старой сетки, 0 для новой.
    const padding = !isNewGrid ? 4 : 0;
    const drawArea: Area = {
        x: area.x + padding,
        y: area.y + padding,
        width: area.width - 2 * padding,
        height: area.height - 2 * padding,
    };

    const params = helpers.getParticipantDrawerParams(
        participant,
        streamToVideosMap,
        isDrawDesktopTrack,
        isDrawPresenter
    );

    return {
        participant,
        refParticipant,
        rawArea: area,
        area: drawArea,
        mode,
        params,
        isDrawDominant: true,
        shouldDrawParticipantStatuses: true,
        participantsCount,
        gridView,
        isMainSlot,
        isMobile,
        isNewGrid,
        isPermissionsExtended,
        hasMicrophonePermission,
        isPresenter,
        isDrawDesktopTrack,
        isDrawPresenter,
    };
}
